import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, type CraneBlockHazard } from '../../../hazards/HazardDefinition';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { hazardSocket, laneX, sidesWithout } from '../hazardBuilders';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  laneArea,
  obstacleRowOn,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import { SegmentKind, type CeilingZone, type FixedObstaclePlacement, type GroundSurface, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_23 – special combo 40 m (design/01_segments/SEG_23_Special_Combo): the sheet's challenge in a row.
 *
 *   z 5       lane closures: both outer lanes (centre open) or the centre lane (lane change –
 *             never two lane changes, playable from any lane at top speed)
 *   z 12      concrete pipes in all lanes (jump)
 *   z 19      barrier boards where the closures left the way open (jump or change lane again)
 *   z 25      low passage "ACHTUNG NIEDRIGE DURCHFAHRT" over the whole track, 1.4 m clearance (slide). Its
 *             box beam ends at 1.9 m, the warning sign on its front face (centre lane) at 2.82 m – both are
 *             their walkable tops: a double jump clears the beam over the outer lanes and, just, the sign
 *             over the centre lane (SEGMENT_STANDARD "Sprungehrlichkeit")
 *   z 29–34   ramp in the centre lane up to 1.2 m, platform z 34–38, drop – risk route: bottles on the
 *             ramp and a high row above the platform end that only a double jump reaches
 *
 * Crane variant – the sheet's dynamic element ("Kranlast"): the closures send the runner to the centre,
 * where a tower crane lowers a concrete block at z 16 (OBS_CRANE_LOAD_B, moving hazard – dodge to an
 * outer lane). No pipes and boards there: at top speed a dodge needs ~10 m after the row before.
 *
 * The Blender overlay (tools/blender/assets/ENV_ComboSet_A.py) is built from these numbers.
 */
export const COMBO = {
  length: 40,
  closureZ: 5,
  pipesZ: 12,
  boardsZ: 19,
  /**
   * Crane beside the right site edge; its backdrop socket stays empty and the two roadside bays at
   * its base (z 0–20, the crane stands on their border) shrink to single ground props.
   */
  craneBlock: { lane: 1, z: 16, crane: { x: 13, z: 10 }, freedSocket: 'SOCKET_DECO_R_04', groundProps: ['SOCKET_DECO_R_01', 'SOCKET_DECO_R_02'] },
  /** Box beam of the height-limit gantry: `depth` along the track, `beam` high (ENV_ComboSet_A). */
  lowPassage: { z: 25, depth: 0.5, clearance: 1.4, beam: 0.5 },
  /**
   * Warning sign on the beam's front face, over the centre lane: a 3.2 × 0.8 m board `lift` above the beam in a
   * `frame`, its face 0.02 m in front of the beam and `thickness` deep including the frame (ENV_ComboSet_A).
   */
  passageSign: { width: 3.2, height: 0.8, lift: 0.08, frame: 0.04, inFront: 0.02, thickness: 0.05 },
  ramp: { lane: 1, zFrom: 29, zTo: 34, height: 1.2 },
  platform: { zFrom: 34, zTo: 38 },
  /** Bottles above head height of a single jump from the platform – only a double jump reaches them. */
  bonusRow: { zFrom: 37.5, zTo: 39.5, lift: 3.3 },
  /** The passage spans the track and the curbs (its posts stand beside the barriers). */
  halfSpan: WORLD_CONFIG.trackWidth / 2 + 0.15,
} as const;

const LANES = [0, 1, 2] as const;
const SPACING = 2;
const { ramp, platform, lowPassage, bonusRow, craneBlock } = COMBO;
const SOCKET_CRANE = 'SOCKET_EVT_CRANE_BLOCK';

const CRANE_BLOCK: CraneBlockHazard = {
  id: 'HAZ_CRANE_BLOCK_L1',
  kind: HazardKind.CraneBlock,
  socketId: SOCKET_CRANE,
  lane: craneBlock.lane,
  z: craneBlock.z,
  crane: craneBlock.crane,
};

const SIDES = sidesWithout(COMBO.length, [craneBlock.freedSocket], craneBlock.groundProps);

const surfaces: GroundSurface[] = [
  { id: 'SURF_RAMP', ...laneArea(WORLD_CONFIG, ramp.lane, ramp.lane, ramp.zFrom, ramp.zTo), heightFrom: 0, heightTo: ramp.height },
  { id: 'SURF_PLATFORM', ...laneArea(WORLD_CONFIG, ramp.lane, ramp.lane, platform.zFrom, platform.zTo), heightFrom: ramp.height, heightTo: ramp.height },
];

/** Top of the passage beam (1.9 m) – its walkable top over the whole track. */
export const PASSAGE_BEAM_TOP = lowPassage.clearance + lowPassage.beam;
const SIGN = COMBO.passageSign;
/** Top of the warning sign's frame over the centre lane (2.82 m). */
export const PASSAGE_SIGN_TOP = PASSAGE_BEAM_TOP + SIGN.lift + SIGN.height + SIGN.frame;
const signFace = lowPassage.z - lowPassage.depth / 2 - SIGN.inFront;

const ceilings: CeilingZone[] = [
  {
    id: 'CEIL_LOW_PASSAGE',
    zFrom: lowPassage.z - lowPassage.depth / 2,
    zTo: lowPassage.z + lowPassage.depth / 2,
    xFrom: -COMBO.halfSpan,
    xTo: COMBO.halfSpan,
    clearance: lowPassage.clearance,
    top: PASSAGE_BEAM_TOP,
  },
  {
    // the sign stands on the beam (a few cm gap) – solid from the beam top up to its frame
    id: 'CEIL_LOW_PASSAGE_SIGN',
    zFrom: signFace,
    zTo: signFace + SIGN.thickness,
    xFrom: -(SIGN.width / 2 + SIGN.frame),
    xTo: SIGN.width / 2 + SIGN.frame,
    clearance: PASSAGE_BEAM_TOP,
    top: PASSAGE_SIGN_TOP,
  },
];

/** Lane closures (Leitpfeiltafeln) at z 5 in the `closed` lanes. */
function closures(closed: readonly number[]): FixedObstaclePlacement[] {
  return closed.map((lane) => ({ spawnPointId: `SOCKET_OBS_C05_L${lane}`, obstacleId: 'OBS_LANE_CLOSURE_A' }));
}

/** Hand-made layout: lane closures in `closed`, pipes in every lane, barrier boards in the lanes the closures left open. */
function layout(closed: readonly number[]): FixedObstaclePlacement[] {
  return [
    ...closures(closed),
    ...LANES.map((lane) => ({ spawnPointId: `SOCKET_OBS_P12_L${lane}`, obstacleId: 'OBS_CONCRETE_PIPE_A' })),
    ...LANES.filter((lane) => !closed.includes(lane)).map((lane) => ({ spawnPointId: `SOCKET_OBS_B19_L${lane}`, obstacleId: 'OBS_BARRIER_BOARD_A' })),
  ];
}

/** Bottle height along the risk route: ground, up the ramp, on the platform. */
function riskLineHeight(z: number): number {
  if (z <= ramp.zFrom) return COLLECTIBLE_FLOAT_HEIGHT;
  if (z <= ramp.zTo) return COLLECTIBLE_FLOAT_HEIGHT + (ramp.height * (z - ramp.zFrom)) / (ramp.zTo - ramp.zFrom);
  return COLLECTIBLE_FLOAT_HEIGHT + ramp.height;
}

export const SEG_COMBO_40M: SegmentDefinition = {
  id: 'SEG_COMBO_40M',
  kind: SegmentKind.Straight,
  displayName: 'Spezial-Kombi 40 m',
  length: COMBO.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_COMBO_40M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 0 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
  ],

  entry: entryConnector(),
  exits: [straightExit(COMBO.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: { surfaces, gaps: [], ceilings },

  obstacleSpawnPoints: [
    ...obstacleRowOn(WORLD_CONFIG, 'C05', COMBO.closureZ, LANES, [ObstacleType.LaneChange]),
    ...obstacleRowOn(WORLD_CONFIG, 'P12', COMBO.pipesZ, LANES, [ObstacleType.Jump]),
    ...obstacleRowOn(WORLD_CONFIG, 'B19', COMBO.boardsZ, LANES, [ObstacleType.Jump]),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    { id: 'CENTRE_OPEN', weight: 1, placements: layout([0, 2]), hazards: [] },
    { id: 'CENTRE_CLOSED', weight: 1, placements: layout([1]), hazards: [] },
    { id: 'CENTRE_CRANE', weight: 1, placements: closures([0, 2]), hazards: [CRANE_BLOCK.id] },
  ],
  hazards: [CRANE_BLOCK],

  collectibleSpawnPoints: [
    ...collectibleLine(WORLD_CONFIG, 'RISK_L1', [
      { lane: ramp.lane, from: 1, to: platform.zTo, spacing: SPACING, height: riskLineHeight },
      { lane: ramp.lane, from: bonusRow.zFrom, to: bonusRow.zTo, spacing: 1, height: () => COLLECTIBLE_FLOAT_HEIGHT + ramp.height + bonusRow.lift },
    ]),
    ...[0, 2].flatMap((lane) =>
      collectibleLine(WORLD_CONFIG, `GROUND_L${lane}`, [{ lane, from: 1, to: COMBO.length - 1, spacing: SPACING, height: () => COLLECTIBLE_FLOAT_HEIGHT }]),
    ),
  ],

  decorationSockets: SIDES.decorationSockets,
  advertisingSlots: SIDES.advertisingSlots,
  dynamicEventSockets: [
    ...SIDES.dynamicEventSockets,
    hazardSocket(SOCKET_CRANE, laneX(WORLD_CONFIG, craneBlock.lane), craneBlock.z, DynamicEventType.CraneLoadSwing),
  ],

  generation: {
    weight: 0.15,
    minDistance: 400,
    minSegmentsAfterJunction: 1,
    family: 'COMBO',
    tags: ['combo', 'terrain', 'ramp', 'ceiling', 'crane', 'hazard'],
  },
};
